import React, { useEffect, useState, useCallback } from "react";
import { Typography, Alert, message } from "antd";
import { connect } from "react-redux";
import dayjs from "dayjs";
import {
  fetchEmailStats,
  fetchEmailEvents,
  fetchEmailTimeline,
  fetchEmailSubjects,
  fetchCampaignStats,
  exportEmailCSV,
  clearEmailAnalytics,
} from "../../store/actions/emailAnalyticsActions";
import StatsCards from "./StatsCards";
import FiltersBar from "./FiltersBar";
import EventsTable from "./EventsTable";
import TimelineChart from "./TimelineChart";
import CampaignFunnelCards from "./CampaignFunnelCards";
import EmailSequenceSection from "./EmailSequenceSection";

const { Title } = Typography;

const EmailAnalytics = ({
  stats,
  events,
  timeline,
  campaigns,
  subjects,
  loading,
  error,
  fetchEmailStats,
  fetchEmailEvents,
  fetchEmailTimeline,
  fetchEmailSubjects,
  fetchCampaignStats,
  clearEmailAnalytics,
}) => {
  // Default to last 30 days
  const defaultStartDate = dayjs().subtract(30, "day").format("YYYY-MM-DD");
  const defaultEndDate = dayjs().format("YYYY-MM-DD");

  const [filters, setFilters] = useState({
    startDate: defaultStartDate,
    endDate: defaultEndDate,
    eventType: "",
    subject: "",
    page: 1,
    limit: 50,
  });

  // Initial data load
  useEffect(() => {
    fetchEmailSubjects();
    loadData();

    return () => {
      clearEmailAnalytics();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = useCallback(() => {
    const { startDate, endDate, eventType, subject, page, limit } = filters;

    fetchEmailStats(startDate, endDate);
    fetchEmailTimeline(startDate, endDate, "day");
    fetchCampaignStats(startDate, endDate, 20);
    fetchEmailEvents({
      startDate,
      endDate,
      eventType,
      subject,
      page,
      limit,
    });
  }, [filters, fetchEmailStats, fetchEmailTimeline, fetchCampaignStats, fetchEmailEvents]);

  const handleFiltersChange = (newFilters) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
      page: newFilters.page !== undefined ? newFilters.page : 1, // Reset page on filter change
    }));
  };

  const handleSearch = () => {
    loadData();
  };

  const handleClear = () => {
    const clearedFilters = {
      startDate: defaultStartDate,
      endDate: defaultEndDate,
      eventType: "",
      subject: "",
      page: 1,
      limit: 50,
    };
    setFilters(clearedFilters);

    // Reload with cleared filters
    fetchEmailStats(defaultStartDate, defaultEndDate);
    fetchEmailTimeline(defaultStartDate, defaultEndDate, "day");
    fetchCampaignStats(defaultStartDate, defaultEndDate, 20);
    fetchEmailEvents({
      startDate: defaultStartDate,
      endDate: defaultEndDate,
      page: 1,
      limit: 50,
    });
  };

  const handleExport = async () => {
    try {
      message.loading({ content: "Gerando CSV...", key: "export" });
      await exportEmailCSV({
        startDate: filters.startDate,
        endDate: filters.endDate,
        eventType: filters.eventType,
      });
      message.success({ content: "CSV exportado com sucesso!", key: "export" });
    } catch (err) {
      message.error({
        content: "Erro ao exportar CSV: " + (err.message || "Erro desconhecido"),
        key: "export",
      });
    }
  };

  const handlePageChange = (page, pageSize) => {
    const newFilters = { ...filters, page, limit: pageSize };
    setFilters(newFilters);

    fetchEmailEvents({
      ...newFilters,
    });
  };

  return (
    <div style={{ padding: "0" }}>
      <Title level={3} style={{ marginBottom: 24 }}>
        Email Analytics
      </Title>

      {error && (
        <Alert
          message="Erro"
          description={error}
          type="error"
          showIcon
          closable
          style={{ marginBottom: 16 }}
        />
      )}

      <FiltersBar
        filters={filters}
        subjects={subjects}
        onFiltersChange={handleFiltersChange}
        onSearch={handleSearch}
        onClear={handleClear}
        onExport={handleExport}
        loading={loading.stats || loading.events}
      />

      <div style={{ marginBottom: 24 }}>
        <StatsCards stats={stats} loading={loading.stats} />
      </div>

      <div style={{ marginBottom: 24 }}>
        <TimelineChart timeline={timeline} loading={loading.timeline} />
      </div>

      <div style={{ marginBottom: 24 }}>
        <CampaignFunnelCards campaigns={campaigns} loading={loading.campaigns} />
      </div>

      <div style={{ marginBottom: 24 }}>
        <EmailSequenceSection />
      </div>

      <div>
        <Title level={5} style={{ marginBottom: 16 }}>
          Detalhes dos Eventos
        </Title>
        <EventsTable
          events={events.events}
          loading={loading.events}
          pagination={{
            page: events.page,
            limit: events.limit,
            total: events.total,
            totalPages: events.totalPages,
          }}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  stats: state.emailAnalytics.stats,
  events: state.emailAnalytics.events,
  timeline: state.emailAnalytics.timeline,
  campaigns: state.emailAnalytics.campaigns,
  subjects: state.emailAnalytics.subjects,
  loading: state.emailAnalytics.loading,
  error: state.emailAnalytics.error,
});

export default connect(mapStateToProps, {
  fetchEmailStats,
  fetchEmailEvents,
  fetchEmailTimeline,
  fetchEmailSubjects,
  fetchCampaignStats,
  clearEmailAnalytics,
})(EmailAnalytics);
